"use client"

import type React from "react"

import { type FormEvent, useState, useRef } from "react"
import { Spinner, Toast } from "react-bootstrap"
import { useTranslation } from "../../hooks/use-translation"
import { ArrowLeft, Paperclip, XCircle, FileText, ImageIcon, File, ChevronDown } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { useAppDispatch, useAppSelector } from "../../redux/hooks"
import { createThread } from "../../redux/slices/messagesSlice"
import { useAuth } from "../../contexts/auth-context"
import UserSelectionModal from "../../components/UserSelectionModal"

export default function NewMessagePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { recipients } = useAppSelector((state) => state.messages)
  const { user } = useAuth()
  const { isLoading } = useAppSelector((state) => state.messages)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState("")
  const [toastVariant, setToastVariant] = useState<"success" | "danger">("success")

  const [recipient, setRecipient] = useState("")
  const [message, setMessage] = useState("")
  const [showUserModal, setShowUserModal] = useState(false)
  const [attachment, setAttachment] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const selectedRecipient = recipients.find((r) => r.id === recipient)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setAttachment(e.target.files[0])
    }
  }

  const clearAttachment = () => {
    setAttachment(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith("image/")) {
      return <ImageIcon size={16} />
    } else if (fileType.includes("pdf")) {
      return <FileText size={16} />
    } else {
      return <File size={16} />
    }
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!recipient || (!message.trim() && !attachment) || !user) return

    const result = await dispatch(
      createThread({
        recipientId: recipient,
        content: message,
        attachment: attachment,
      }),
    )

    // Check if the action was fulfilled or rejected
    if (result.meta.requestStatus === "fulfilled") {
      setToastVariant("success")
      setToastMessage(t("messageSentDescription"))
      setShowToast(true)

      const payload = result.payload as { success: boolean; threadId: string }
      if (payload.threadId) {
        setTimeout(() => navigate(`/dashboard/messages/${payload.threadId}`), 1500)
      } else {
        setTimeout(() => navigate("/dashboard/messages"), 1500)
      }
    } else {
      setToastVariant("danger")
      setToastMessage(t("messageErrorDescription"))
      setShowToast(true)
    }
  }

  return (
    <>
      <div
        className="d-md-none"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          height: "70px",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          zIndex: 1030,
        }}
      >
        <div className="d-flex align-items-center h-100 px-3 gap-3">
          <button className="btn-back-circle flex-shrink-0" onClick={() => navigate(-1)}>
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-white mb-0 flex-grow-1" style={{ fontSize: "1.25rem", fontWeight: "600" }}>
            {t("newMessage")}
          </h1>
        </div>
      </div>

      <div className="d-md-none" style={{ paddingTop: "70px", padding: "70px 1rem 2rem 1rem" }}>
        <div className="container">
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="text-white mb-3" style={{ fontSize: "1.5rem", fontWeight: "300" }}>
                {t("to")}
              </label>
              <div className="position-relative">
                <div
                  className="form-control d-flex align-items-center justify-content-between"
                  style={{
                    background: "white",
                    border: "none",
                    borderRadius: "16px",
                    padding: "1rem 1.5rem",
                    fontSize: "1.1rem",
                    cursor: "pointer",
                    minHeight: "60px",
                  }}
                  onClick={() => setShowUserModal(true)}
                >
                  <span style={{ color: selectedRecipient ? "#333" : "#999" }}>
                    {selectedRecipient ? selectedRecipient.name : t("selectRecipient")}
                  </span>
                  <ChevronDown size={20} style={{ color: "#666" }} />
                </div>
              </div>
            </div>

            <div className="mb-4">
              <label className="text-white mb-3" style={{ fontSize: "1.5rem", fontWeight: "300" }}>
                {t("message")}
              </label>
              <textarea
                className="form-control"
                rows={6}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t("typeYourMessage")}
                required={!attachment}
                style={{
                  background: "white",
                  border: "none",
                  borderRadius: "16px",
                  padding: "1.5rem",
                  fontSize: "1.1rem",
                  resize: "none",
                }}
              />
            </div>

            <div className="mb-4">
              <label className="text-white mb-3" style={{ fontSize: "1.5rem", fontWeight: "300" }}>
                {t("attachment")} ({t("optional")})
              </label>
              <div className="d-flex align-items-center gap-3">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="btn btn-transparent-outline d-flex align-items-center gap-2"
                >
                  <Paperclip size={16} />
                  {t("attachFile")}
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                />
                {attachment && <span className="text-white small opacity-75">{attachment.name}</span>}
              </div>
              {attachment && (
                <div
                  className="mt-3 p-3 d-flex justify-content-between align-items-center"
                  style={{
                    background: "rgba(255, 255, 255, 0.1)",
                    borderRadius: "12px",
                  }}
                >
                  <div className="d-flex align-items-center gap-2 text-white">
                    {getFileIcon(attachment.type)}
                    <span className="text-truncate">{attachment.name}</span>
                  </div>
                  <button
                    type="button"
                    onClick={clearAttachment}
                    className="btn p-0"
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "white",
                    }}
                  >
                    <XCircle size={20} />
                  </button>
                </div>
              )}
            </div>

            <div className="d-flex justify-content-end gap-3 mt-4">
              <button type="button" onClick={() => navigate(-1)} className="btn btn-cancel-outline">
                {t("cancel")}
              </button>
              <button
                type="submit"
                disabled={isLoading || !selectedRecipient || (!message.trim() && !attachment)}
                className="btn btn-submit-white"
              >
                {isLoading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                    {t("sending")}
                  </>
                ) : (
                  t("sendMessage")
                )}
              </button>
            </div>
          </form>

          <UserSelectionModal
            show={showUserModal}
            onHide={() => setShowUserModal(false)}
            onSelect={setRecipient}
            selectedUserId={recipient}
          />

          <Toast
            show={showToast}
            onClose={() => setShowToast(false)}
            delay={3000}
            autohide
            className="position-fixed bottom-0 end-0 m-3"
            bg={toastVariant}
          >
            <Toast.Header>
              <strong className="me-auto">{toastVariant === "success" ? "Success" : "Error"}</strong>
            </Toast.Header>
            <Toast.Body className={toastVariant === "danger" ? "text-white" : undefined}>{toastMessage}</Toast.Body>
          </Toast>
        </div>
      </div>

      <div className="d-none d-md-block" style={{ padding: "2rem 1rem" }}>
        <div className="container">
          <div className="d-flex align-items-center gap-3 mb-4">
            <button onClick={() => navigate(-1)} className="btn-back-circle">
              <ArrowLeft size={20} />
            </button>
          </div>

          <h1 className="text-white mb-2" style={{ fontSize: "3rem", fontWeight: "bold" }}>
            {t("newMessage")}
          </h1>

          <h2 className="text-white mb-5" style={{ fontSize: "2rem", fontWeight: "300", opacity: 0.9 }}>
            {t("composeMessage")}
          </h2>

          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="text-white mb-3" style={{ fontSize: "1.5rem", fontWeight: "300" }}>
                {t("to")}
              </label>
              <div className="position-relative">
                <div
                  className="form-control d-flex align-items-center justify-content-between"
                  style={{
                    background: "white",
                    border: "none",
                    borderRadius: "16px",
                    padding: "1rem 1.5rem",
                    fontSize: "1.1rem",
                    cursor: "pointer",
                    minHeight: "60px",
                  }}
                  onClick={() => setShowUserModal(true)}
                >
                  <span style={{ color: selectedRecipient ? "#333" : "#999" }}>
                    {selectedRecipient ? selectedRecipient.name : t("selectRecipient")}
                  </span>
                  <ChevronDown size={20} style={{ color: "#666" }} />
                </div>
              </div>
            </div>

            <div className="mb-4">
              <label className="text-white mb-3" style={{ fontSize: "1.5rem", fontWeight: "300" }}>
                {t("message")}
              </label>
              <textarea
                className="form-control"
                rows={6}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t("typeYourMessage")}
                required={!attachment}
                style={{
                  background: "white",
                  border: "none",
                  borderRadius: "16px",
                  padding: "1.5rem",
                  fontSize: "1.1rem",
                  resize: "none",
                }}
              />
            </div>

            <div className="mb-4">
              <label className="text-white mb-3" style={{ fontSize: "1.5rem", fontWeight: "300" }}>
                {t("attachment")} ({t("optional")})
              </label>
              <div className="d-flex align-items-center gap-3">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="btn btn-transparent-outline d-flex align-items-center gap-2"
                >
                  <Paperclip size={16} />
                  {t("attachFile")}
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                />
                {attachment && <span className="text-white small opacity-75">{attachment.name}</span>}
              </div>
              {attachment && (
                <div
                  className="mt-3 p-3 d-flex justify-content-between align-items-center"
                  style={{
                    background: "rgba(255, 255, 255, 0.1)",
                    borderRadius: "12px",
                  }}
                >
                  <div className="d-flex align-items-center gap-2 text-white">
                    {getFileIcon(attachment.type)}
                    <span className="text-truncate">{attachment.name}</span>
                  </div>
                  <button
                    type="button"
                    onClick={clearAttachment}
                    className="btn p-0"
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "white",
                    }}
                  >
                    <XCircle size={20} />
                  </button>
                </div>
              )}
            </div>

            <div className="d-flex justify-content-end gap-3 mt-4">
              <button type="button" onClick={() => navigate(-1)} className="btn btn-cancel-outline">
                {t("cancel")}
              </button>
              <button
                type="submit"
                disabled={isLoading || !selectedRecipient || (!message.trim() && !attachment)}
                className="btn btn-submit-white"
              >
                {isLoading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                    {t("sending")}
                  </>
                ) : (
                  t("sendMessage")
                )}
              </button>
            </div>
          </form>

          <UserSelectionModal
            show={showUserModal}
            onHide={() => setShowUserModal(false)}
            onSelect={setRecipient}
            selectedUserId={recipient}
          />

          <Toast
            show={showToast}
            onClose={() => setShowToast(false)}
            delay={3000}
            autohide
            className="position-fixed bottom-0 end-0 m-3"
            bg={toastVariant}
          >
            <Toast.Header>
              <strong className="me-auto">{toastVariant === "success" ? "Success" : "Error"}</strong>
            </Toast.Header>
            <Toast.Body className={toastVariant === "danger" ? "text-white" : undefined}>{toastMessage}</Toast.Body>
          </Toast>
        </div>
      </div>
    </>
  )
}
